import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useRotateWebhookSecret } from "@/queries/webhook-queries";
import { formatError } from "@/utils/errors";
import { WebhookProvider } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { useCallback, useMemo, type FC } from "react";

type RotateWebhookSecretDialogProps = {
    webhookId: string;
    provider: WebhookProvider;
    open: boolean;
    onClose: () => void;
};

export const RotateWebhookSecretDialog: FC<RotateWebhookSecretDialogProps> = ({
    webhookId,
    provider,
    open,
    onClose,
}) => {
    const { toast } = useToast();
    const { mutateAsync: rotateSecret, isPending: isRotating } = useRotateWebhookSecret();

    const providerName = useMemo(() => {
        switch (provider) {
            case WebhookProvider.GITHUB:
                return "GitHub";
            case WebhookProvider.GITLAB:
                return "GitLab";
            default:
                return "your SCM provider";
        }
    }, [provider]);

    const handleRotate = useCallback(async () => {
        try {
            await rotateSecret(webhookId);
            toast({
                title: "Webhook secret has been rotated",
            });
            onClose();
        } catch (error) {
            toast({
                title: "Failed to rotate webhook secret",
                description: formatError(error),
            });
        }
    }, [webhookId, rotateSecret, toast, onClose]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="rotate-webhook-secret-dialog"
                data-track-location={TrackLocations.RotateWebhookSecretDialog}
            >
                <DialogHeader className="mb-0">
                    <DialogTitle>Rotate webhook secret</DialogTitle>
                    <DialogDescription>This will invalidate the current secret token.</DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <div className="rounded-lg border border-border-warning px-4 py-3 text-content-primary">
                        <div className="flex gap-3">
                            <IconTriangleExclamation size={16} className="mt-0.5 shrink-0 text-content-warning" />
                            <div className="flex flex-col gap-3">
                                <p>
                                    The current secret will stop working <strong>immediately</strong> after rotation.
                                </p>
                                <p>
                                    You will need to update the webhook secret in {providerName} for signature
                                    verification to continue working.
                                </p>
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="rotate-webhook-secret-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleRotate}
                        loading={isRotating}
                        disabled={isRotating}
                        data-testid="rotate-webhook-secret-dialog-confirm"
                        data-tracking-id="confirm-rotate-secret-rotate-webhook-secret-dialog"
                    >
                        Rotate secret
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
